import { ReactNode, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import dayjs from 'dayjs';

import { useApiClient } from '../../../../core/libs/api/api-client.provider';
import { HttpResource } from '../../../../core/libs/http-resource';
import { AppNotify } from '../../../../features/app-notify/app-notify';
import { EOrderStatus, TOrder, TOrderTicket } from '../../../orders/core/order';
import { cancelOrderRequest } from '../../requests/cancel_order.request';

type RefundStateItem = {
  checkVisible: (order: TOrder, ticket?: TOrderTicket) => boolean;
  actionTitle: string;
  description: ReactNode;
};

export const checkRefundForPayedTicket = (
  ticket?: TOrderTicket,
  refundStopDelay = -5 // Minutes. Must be taken from ticket/order data. Waiting for backend api realization
) => {
  if (!ticket) return false;

  const departureTime = dayjs(ticket?.departureTime);
  const stopRefundTime = departureTime.add(refundStopDelay, 'minutes');
  const isRefundOpen = stopRefundTime.isAfter(dayjs());

  return isRefundOpen;
};

const getRefundStates = (status: EOrderStatus): RefundStateItem => {
  switch (status) {
    case EOrderStatus.new:
      return {
        checkVisible: (order, ticket) =>
          checkRefundForPayedTicket(ticket, order?.refundStopDelay),
        actionTitle: t`Вернуть билет`,
        description: <Trans>Вы уверены, что хотите отменить заказ?</Trans>,
      };
    case EOrderStatus.success:
      return {
        checkVisible: (order, ticket) =>
          checkRefundForPayedTicket(ticket, order?.refundStopDelay),
        actionTitle: t`Вернуть билет`,
        description: (
          <>
            <Trans>Вы уверены, что хотите отменить заказ?</Trans> <br />
            <Trans>
              Стоимость оплаченного билета будет возвращена с помощью выбранного
              способа оплаты (банковская карта)
            </Trans>
          </>
        ),
      };

    case EOrderStatus.booked:
      return {
        checkVisible: () => true,
        actionTitle: t`Отменить заказ`,
        description: <Trans>Вы уверены, что хотите отменить заказ?</Trans>,
      };

    case EOrderStatus.error:
      return {
        checkVisible: () => false,
        actionTitle: t`Скрыть заказ`,
        description: <Trans>Заказ с ошибкой будет скрыт</Trans>,
      };

    case EOrderStatus.canceled:
      return {
        checkVisible: () => false,
        actionTitle: t`Скрыть заказ`,
        description: <Trans>Отмененный заказ будет скрыт</Trans>,
      };

    default:
      return {
        checkVisible: () => false,
        actionTitle: `Неизвестный статус заказа`,
        description: (
          <Trans>Что-то пошло не так. Неизвестный статус заказа</Trans>
        ),
      };
  }
};

type UseTicketRefundPatams = {
  order: TOrder;
  ticket?: TOrderTicket;
  onOrderUpdate: (order: TOrder) => void;
};
type OrderRequestArg = Partial<TOrder> & Pick<TOrder, 'id' | 'tickets'>;

export const useTicketRefund = ({
  order,
  ticket,
  onOrderUpdate,
}: UseTicketRefundPatams) => {
  const apiClient = useApiClient();

  const [refundTicketRequest] = useState(
    () =>
      new HttpResource<OrderRequestArg, TOrder>({
        apiClient,
        httpRequest: cancelOrderRequest,
        errorNotify: new AppNotify(),
        onFinish(result) {
          onOrderUpdate(result);
        },
      })
  );

  const orderState = getRefundStates(order.status);

  return {
    isVisible: () => orderState.checkVisible(order, ticket),
    loading: refundTicketRequest.isLoading,
    actionTitle: orderState.actionTitle,
    description: orderState.description,
    cancelOrder: () => refundTicketRequest.send(order),
  };
};
